:root {
    --primary: #d97706;
    --primary-hover: #b45309;
    --bg: #0a0a0a;
    --sidebar-bg: linear-gradient(180deg, #1a1a1a 0%, #141414 100%);
    --card-bg: linear-gradient(135deg, #1f1f1f 0%, #1a1a1a 100%);
    --input-bg: linear-gradient(135deg, #161616 0%, #121212 100%);
    --text: #e5e5e5;
    --text-muted: #a3a3a3;
    --border: #2a2a2a;
    --danger: #ef4444;
    --success: #22c55e;
    --warning: #eab308;
}

* {
    box-sizing: border-box;
}

body {
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
    background-color: var(--bg);
    color: var(--text);
    margin: 0;
    padding: 0;
    height: 100vh;
    overflow: hidden;
}

/* Layout */
.app-container {
    display: flex;
    height: 100vh;
}

.sidebar {
    width: 260px;
    background: var(--sidebar-bg);
    border-right: 1px solid var(--border);
    display: flex;
    flex-direction: column;
    padding: 1.5rem;
    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.3);
}

.main-content {
    flex: 1;
    overflow-y: auto;
    padding: 2rem;
    background: var(--bg);
}

/* Sidebar */
.logo {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 2.5rem;
}

.logo-icon {
    display: flex;
}

.logo h1 {
    font-size: 1.25rem;
    font-weight: 700;
    margin: 0;
    background: linear-gradient(to right, #e5e5e5, #737373);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
}

.nav-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    color: var(--text-muted);
    text-decoration: none;
    border-radius: 0.5rem;
    margin-bottom: 0.5rem;
    transition: all 0.2s;
    font-weight: 500;
}

.nav-item:hover {
    background: rgba(255, 255, 255, 0.05);
    color: var(--text);
}

.nav-item.active {
    background: var(--primary);
    color: white;
}

.nav-bottom {
    margin-top: auto;
    padding-top: 1rem;
    border-top: 1px solid var(--border);
}

/* Pages */
.page-container {
    max-width: 1000px;
    margin: 0 auto;
}

.page-header {
    margin-bottom: 2rem;
}

.page-header h2 {
    font-size: 1.875rem;
    font-weight: 700;
    margin: 0 0 0.5rem 0;
}

.page-header p {
    color: var(--text-muted);
    margin: 0;
}

.content-card {
    background: var(--card-bg);
    border: 1px solid var(--border);
    border-radius: 1rem;
    padding: 2rem;
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.4);
}

/* Forms */
.form-group {
    margin-bottom: 1.5rem;
}

label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    margin-bottom: 0.5rem;
    color: var(--text-muted);
}

input,
select,
textarea {
    width: 100%;
    background: var(--input-bg);
    border: 1px solid var(--border);
    border-radius: 0.5rem;
    padding: 0.75rem;
    color: var(--text);
    font-size: 0.95rem;
    transition: border-color 0.2s, box-shadow 0.2s;
    box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.3);
}

input:focus,
select:focus,
textarea:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
}

/* Buttons */
.btn-primary {
    background: var(--primary);
    color: white;
    border: none;
    border-radius: 0.5rem;
    padding: 0.75rem 1.5rem;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    transition: background 0.2s;
}

.btn-primary:hover {
    background: var(--primary-hover);
}

.btn-primary:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.btn-secondary {
    background: transparent;
    border: 1px dashed var(--border);
    color: var(--text-muted);
    padding: 0.75rem;
    border-radius: 0.5rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    transition: all 0.2s;
}

.btn-secondary:hover {
    border-color: var(--text-muted);
    color: var(--text);
}

.btn-icon {
    background: transparent;
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 0.25rem;
    transition: color 0.2s;
}

.btn-icon:hover {
    color: var(--text);
}

.btn-icon.danger:hover {
    color: var(--danger);
}

.full-width {
    width: 100%;
}

.mt-4 {
    margin-top: 1rem;
}

.mt-6 {
    margin-top: 1.5rem;
}

/* Tabs */
.tabs {
    display: flex;
    gap: 1rem;
    margin-bottom: 2rem;
    border-bottom: 1px solid var(--border);
    padding-bottom: 1rem;
}

.tab {
    background: transparent;
    border: none;
    color: var(--text-muted);
    font-weight: 500;
    cursor: pointer;
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    transition: all 0.2s;
}

.tab.active {
    background: var(--primary);
    color: white;
}

/* Batch Lists */
.input-row {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
}

.dynamic-table-wrapper {
    overflow-x: auto;
}

.dynamic-table {
    width: 100%;
    border-collapse: collapse;
}

.dynamic-table th {
    text-align: left;
    padding: 0.75rem;
    color: var(--text-muted);
    font-weight: 500;
    border-bottom: 1px solid var(--border);
}

.dynamic-table td {
    padding: 0.5rem;
}

/* History Grid */
.video-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1.5rem;
}

.video-card {
    background: var(--card-bg);
    border: 1px solid var(--border);
    border-radius: 0.75rem;
    overflow: hidden;
    transition: transform 0.2s, box-shadow 0.2s;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.video-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.5);
}

.video-preview {
    aspect-ratio: 16/9;
    background: #000;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
}

.video-preview.portrait {
    aspect-ratio: 9/16;
}

.video-preview video {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.status-overlay {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    color: var(--text-muted);
}

.status-icon {
    width: 24px;
    height: 24px;
}

.status-icon.queued {
    color: var(--warning);
}

.status-icon.processing {
    color: #38bdf8;
}

.status-icon.failed {
    color: var(--danger);
}

.status-text {
    font-size: 0.75rem;
    text-transform: uppercase;
    font-weight: 600;
    letter-spacing: 0.05em;
}

.badge-container {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
}

.mode-badge {
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    font-size: 0.7rem;
    text-transform: uppercase;
    font-weight: 600;
}

.video-details {
    padding: 1rem;
}

.video-prompt {
    font-size: 0.875rem;
    margin: 0 0 0.5rem 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.video-meta {
    display: flex;
    justify-content: space-between;
    font-size: 0.75rem;
    color: var(--text-muted);
}

/* Header Controls */
.header-content {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
}

.filter-controls {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: var(--input-bg);
    padding: 0.25rem 0.75rem;
    border-radius: 0.5rem;
    border: 1px solid var(--border);
}

.filter-select {
    border: none;
    background: transparent;
    padding: 0.25rem;
    width: auto;
}

.filter-select:focus {
    box-shadow: none;
}

/* Modal */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
}

.modal {
    background: var(--card-bg);
    border: 1px solid var(--border);
    border-radius: 1rem;
    width: 90%;
    max-width: 400px;
    padding: 1.5rem;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.6);
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
}

.modal-header h3 {
    margin: 0;
}

.progress-bar-container {
    height: 8px;
    background: var(--bg);
    border-radius: 4px;
    overflow: hidden;
}

.progress-bar {
    height: 100%;
    background: var(--primary);
    transition: width 0.3s ease;
}

.animate-spin {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

/* Generation Alert Styles */
.generation-alert-overlay {
    z-index: 200;
}

.generation-alert-modal {
    max-width: 480px;
    width: 90%;
}

.generation-alert-header {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    margin-bottom: 1rem;
}

.alert-icon-container {
    display: flex;
    justify-content: center;
    margin-bottom: 0.5rem;
}

.alert-icon {
    display: block;
}

.alert-icon.warning {
    color: var(--warning);
}

.alert-icon.success {
    color: var(--success);
}

.alert-icon.error {
    color: var(--danger);
}

.alert-close-btn {
    position: absolute;
    top: -0.5rem;
    right: -0.5rem;
    background: var(--card-bg);
    border: 1px solid var(--border);
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.alert-close-btn:hover {
    background: var(--input-bg);
}

.generation-alert-body {
    text-align: center;
    margin-bottom: 2rem;
}

.alert-title {
    font-size: 1.25rem;
    font-weight: 600;
    margin: 0 0 0.75rem 0;
    color: var(--text);
}

.alert-message {
    font-size: 0.95rem;
    color: var(--text-muted);
    line-height: 1.5;
    margin: 0;
}

.generation-alert-footer {
    display: flex;
    gap: 0.75rem;
    justify-content: center;
    flex-wrap: wrap;
}

.alert-primary-btn {
    min-width: 140px;
}

.alert-secondary-btn {
    min-width: 100px;
    background: transparent;
    border: 1px solid var(--border);
    color: var(--text-muted);
}

.alert-secondary-btn:hover {
    border-color: var(--text-muted);
    color: var(--text);
    background: rgba(255, 255, 255, 0.05);
}

@media (max-width: 768px) {
    .app-container {
        flex-direction: column;
    }

    .sidebar {
        width: 100%;
        height: auto;
        order: 2;
        border-right: none;
        border-top: 1px solid var(--border);
        flex-direction: row;
        justify-content: space-around;
        padding: 0.5rem;
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 50;
        background: var(--sidebar-bg);
    }

    .logo {
        display: none;
    }

    .main-content {
        order: 1;
        padding: 1rem;
        padding-bottom: 80px;
        /* Space for bottom nav */
        width: 100%;
    }

    .nav-item {
        flex-direction: column;
        gap: 0.25rem;
        padding: 0.5rem;
        font-size: 0.75rem;
        margin-bottom: 0;
        border-radius: 0.5rem;
    }

    .nav-item span {
        display: block;
    }

    .generation-alert-footer {
        flex-direction: column;
    }

    .alert-primary-btn,
    .alert-secondary-btn {
        width: 100%;
    }
}
